import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { resourcesConfig } from '../../resourcesConfig.js';

import ResourceCard from './ResourceCard.jsx';
import { CARD_SIZES } from '../utils.js';

/**
 * Listado genérico de recursos.
 *
 * Renderiza un nivel del "árbol" de un recurso a partir de su adaptador
 * (`resourcesConfig`). Es recursivo: al hacer click en un item contenedor se
 * profundiza en otra lista; en una hoja se irá al detalle (pendiente).
 *
 * Está desacoplado del chrome de la página para poder reutilizarse en otras
 * partes de la plataforma. La conexión con el router la hace ResourcesListWrapper.
 *
 * Props:
 * - resourceType: clave del recurso ('laminaria' | 'videos' | 'anatomical-collections').
 * - size: 'small' | 'medium' | 'large'. Escala de los cards y del espaciado de la grilla.
 * - pathSegments: rastro de ids desde la raíz hasta el nivel actual.
 * - showBackButton: muestra el botón "Volver".
 * - basePath: ruta base para construir la navegación (por defecto /resources/:resourceType).
 * - onDrillDown(item): override al entrar a un contenedor. Default: navega por URL.
 * - onBack(): override del botón "Volver". Default: navega hacia atrás por URL.
 * - onOpenDetail(item): override al abrir una hoja. Default: abre el detalle en pestaña nueva.
 * - mode: 'view' | 'select'. En 'select' las hojas muestran botón de seleccionar.
 * - selectedKeys: ids de las hojas seleccionadas (para marcar el estado del card).
 * - onSelect(item): callback al alternar la selección de una hoja.
 *
 * Los tres callbacks de navegación permiten usar el componente embebido (fuera de
 * las rutas de /resources) controlando el rastro con estado local en vez de la URL.
 */
const ResourcesList = ({
  resourceType,
  size = 'medium',
  pathSegments = [],
  showBackButton = true,
  basePath,
  onDrillDown,
  onBack,
  onOpenDetail,
  mode = 'view',
  selectedKeys = [],
  onSelect = null,
}) => {
  const navigate = useNavigate();
  const config = resourcesConfig[resourceType];

  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const root = basePath || `/resources/${resourceType}`;
  const segmentsKey = pathSegments.join('/');
  const currentPath = segmentsKey ? `${root}/${segmentsKey}` : root;

  useEffect(() => {
    if (!config) {
      setError('Tipo de recurso no válido.');
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    const fetchLevel = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await config.getLevel(pathSegments);
        if (mounted) {
          setLevel(data);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              'No se pudieron cargar los recursos. Intenta nuevamente.'
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchLevel();
    return () => {
      mounted = false;
    };
  }, [resourceType, segmentsKey]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (pathSegments.length > 0) {
      const parentSegments = pathSegments.slice(0, -1);
      navigate(parentSegments.length ? `${root}/${parentSegments.join('/')}` : root);
    } else {
      navigate('/resources');
    }
  };

  const handleItemClick = item => {
    if (item.kind === 'container') {
      if (onDrillDown) {
        onDrillDown(item);
        return;
      }
      navigate(`${currentPath}/${item.id}`);
    } else {
      if (onOpenDetail) {
        onOpenDetail(item);
        return;
      }
      const detailPath = `${root}/detail/${[...pathSegments, item.id].join('/')}`;
      window.open(detailPath, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const items = level?.items || [];

  const stylesBySize = CARD_SIZES[size] || CARD_SIZES.medium;

  return (
    <Box>
      {showBackButton && (
        <Box display='flex' justifyContent='flex-end' mb={3}>
          <Button
            variant='contained'
            size={size === 'small' ? 'small' : 'medium'}
            onClick={handleBack}
            startIcon={<ArrowBackIosIcon />}>
            Volver
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!error && items.length === 0 ? (
        <Alert severity='info'>No hay recursos disponibles en esta sección.</Alert>
      ) : (
        <Grid container spacing={stylesBySize.containerSpacing} justifyContent='center'>
          {items.map((item, index) => (
            <ResourceCard
              key={`${item.kind}-${item.id ?? index}`}
              size={size}
              title={item.title}
              image={item.image}
              icon={item.icon}
              kindLabel={item.kindLabel}
              ctaLabel={item.ctaLabel}
              onClick={() => handleItemClick(item)}
              mode={mode}
              selectable={item.kind === 'leaf'}
              isSelected={item.kind === 'leaf' && selectedKeys.includes(String(item.id))}
              onSelect={() => onSelect && onSelect(item)}
            />
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ResourcesList;
